import { useMemo } from 'react';
import { SelectProps } from '@radix-ui/react-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@antimatterhq/ui/components/select';
import { Text } from '@antimatterhq/ui/components/typography';

const options = [
  {
    format: 'JSON',
    description:
      'JavaScript Object Notation, a lightweight data interchange format',
  },
  {
    format: 'JSONL',
    description: 'JSON Lines, every line is a separate JSON object',
  },
  {
    format: 'CSV',
    description: 'Comma-Separated Values, a simple tabular data format',
  },
  {
    format: 'XML',
    description:
      'eXtensible Markup Language, a markup language for structured data',
  },
  {
    format: 'Parquet',
    description: 'A columnar storage format optimized for analytics',
  },
  {
    format: 'Avro',
    description: 'A data serialization system with rich data structures',
  },
  {
    format: 'Text',
    description: 'A plain text format where every line is a separate record',
  },
];

export type DataFormatDropdownProps = {
  onBlur?: () => void;
  'data-testid'?: string;
} & Pick<SelectProps, 'value' | 'onValueChange' | 'name' | 'disabled'>;

const FormatSelected = ({ name }: { name?: string }) => {
  return <Text>{name}</Text>;
};

const FormatOption = ({
  name,
  description,
}: {
  name?: string;
  description?: string;
}) => {
  return (
    <div className="flex gap-2">
      <Text>{name}</Text>
      <Text color="info">—</Text>
      <Text color="info">{description}</Text>
    </div>
  );
};

export default function DataCollectionMethodsDropdown({
  value,
  onValueChange,
  onBlur,
  'data-testid': dataTestId,
  ...props
}: DataFormatDropdownProps) {
  const selectedOption = useMemo(
    () => options.find((option) => option.format.toLocaleLowerCase() === value),
    [value],
  );

  return (
    <Select value={value} onValueChange={onValueChange} {...props}>
      <SelectTrigger onBlur={onBlur} data-testid={`${dataTestId}`}>
        <SelectValue placeholder="Select a format">
          {selectedOption && <FormatSelected name={selectedOption.format} />}
        </SelectValue>
        <SelectContent>
          {options.map((option, index) => (
            <SelectItem
              key={option.format}
              value={option.format.toLocaleLowerCase()}
              data-testid={`${dataTestId}-option-${index}`}
            >
              <FormatOption
                name={option.format}
                description={option.description}
              />
            </SelectItem>
          ))}
        </SelectContent>
      </SelectTrigger>
    </Select>
  );
}
